import type { ComponentProps, MouseEvent, ReactElement, ReactNode } from "react";
import { useRouter } from "next/router";
import IconButton from "@Front/Components/DesignSystem/IconButton";
import { hardClientNavigate } from "@Front/Utils/hardClientNavigate";

export type IconNavigationButtonProps = Omit<ComponentProps<typeof IconButton>, "onClick" | "ariaLabel"> & {
	href: string;
	ariaLabel: string;
	wrapperClassName?: string | undefined;
	wrapperTitle?: string | undefined;
	onClick?: React.MouseEventHandler<HTMLSpanElement> | undefined;
};

/**
 * Icon action that navigates with router.push (avoids &lt;Link&gt; around IconButton / nested interactive targets).
 */
export function IconNavigationButton(props: IconNavigationButtonProps): ReactElement {
	const router = useRouter();
	const { href, wrapperClassName, wrapperTitle, onClick: externalOnClick, ariaLabel, ...iconProps } = props;
	const handleClick = (e: MouseEvent<HTMLSpanElement>): void => {
		externalOnClick?.(e);
		void router.push(href);
	};
	return (
		<span className={wrapperClassName} title={wrapperTitle}>
			<IconButton {...iconProps} ariaLabel={ariaLabel} onClick={handleClick} />
		</span>
	);
}

export type TextLinkNavigationButtonProps = {
	href: string;
	className?: string | undefined;
	children: ReactNode;
};

/**
 * Text styled as a link; uses a button + router.push (avoids &lt;Link&gt; around block/typography children).
 */
export function TextLinkNavigationButton(props: TextLinkNavigationButtonProps): ReactElement {
	const router = useRouter();
	const { href, className, children } = props;
	return (
		<button type="button" className={className} onClick={() => void router.push(href)}>
			{children}
		</button>
	);
}

export type ChildrenNavigationButtonProps = {
	href: string;
	className?: string | undefined;
	children: ReactNode;
	"aria-label"?: string | undefined;
	title?: string | undefined;
	/**
	 * soft (default): Next router.push.
	 * hard: window.location.assign — use when soft-nav updates history without remounting the target page
	 * (e.g. deed-types detail → /edit on stable dashboard shell).
	 */
	navigationMode?: "soft" | "hard" | undefined;
};

/**
 * Arbitrary children + router.push (e.g. next/image, thumbnail preview). Prefer over &lt;Link&gt; wrapping interactive-looking surfaces.
 */
export function ChildrenNavigationButton(props: ChildrenNavigationButtonProps): ReactElement {
	const router = useRouter();
	const { href, className, children, "aria-label": ariaLabel, title, navigationMode = "soft" } = props;
	return (
		<button
			type="button"
			className={className}
			aria-label={ariaLabel}
			title={title}
			onClick={(event: MouseEvent<HTMLButtonElement>): void => {
				event.preventDefault();
				event.stopPropagation();
				if (navigationMode === "hard") {
					hardClientNavigate(href, "ChildrenNavigationButton");
					return;
				}
				void router.push(href);
			}}>
			{children}
		</button>
	);
}
